import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Package, TrendingUp, TrendingDown, AlertTriangle, Flame, Clock } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const fmt     = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
const fmtQty  = (n) => n != null ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 1 }) : '—';
const fmtPct  = (n) => n != null ? `${Number(n).toFixed(1)}%` : '—';

function StatCard({ label, value, sub, valueClass = '' }) {
  return (
    <div className="bg-muted/40 rounded-xl p-3 flex flex-col gap-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold leading-tight ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function RiskBadge({ icon: Icon, color, label, detail }) {
  const styles = {
    red:    'bg-red-50 border-red-200 text-red-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };
  return (
    <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${styles[color] || styles.amber}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-semibold">{label}</p>
        {detail && <p className="text-xs opacity-80 mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

const WAC_COLOR  = '#6366f1';
const WASP_COLOR = '#10b981';

const ProductDetailModal = ({ product, wac, wasp, reorderInfo, topInfo, deadInfo, leakageInfo, onClose }) => {
  if (!product) return null;

  const marginValue = wac && wasp && wac > 0 ? ((wasp - wac) / wac) * 100 : null;
  const marginColor = marginValue == null ? 'text-muted-foreground'
    : marginValue < 0  ? 'text-red-600'
    : marginValue < 10 ? 'text-amber-600'
    : 'text-emerald-700';

  const velocity     = reorderInfo?.velocity ?? 0;
  const daysLeft     = velocity > 0 && product.stock > 0 ? Math.round(product.stock / velocity) : null;
  const revAtRisk    = reorderInfo?.rev_at_risk;
  const isOOS        = reorderInfo?.is_out_of_stock;
  const isLowStock   = reorderInfo?.is_low_stock && !isOOS;
  const isDead       = !!deadInfo;
  const hasLeakage   = leakageInfo && (leakageInfo.total_loss > 0 || leakageInfo.loss_per_unit > 0);

  const chartData = wac && wasp ? [
    { name: 'Avg Buy (WAC)',  value: parseFloat(wac.toFixed(2)),  fill: WAC_COLOR  },
    { name: 'Avg Sell (WASP)', value: parseFloat(wasp.toFixed(2)), fill: WASP_COLOR },
  ] : null;

  const packLabel = [product.pack_size, product.unit].filter(Boolean).join(' ');
  const stockStatus = isOOS ? { label: 'Out of Stock', cls: 'bg-red-100 text-red-700' }
    : isLowStock        ? { label: 'Low Stock',     cls: 'bg-amber-100 text-amber-700' }
    : isDead            ? { label: 'Dead Stock',    cls: 'bg-slate-100 text-slate-600' }
    : product.stock > 0 ? { label: 'In Stock',      cls: 'bg-emerald-100 text-emerald-700' }
    : { label: 'No Data', cls: 'bg-muted text-muted-foreground' };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b bg-muted/20">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="font-bold text-base leading-tight truncate">{product.name}</h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {product.category && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{product.category}</span>
                  )}
                  {product.sku && (
                    <span className="text-xs text-muted-foreground font-mono">{product.sku}</span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${stockStatus.cls}`}>{stockStatus.label}</span>
                </div>
                {packLabel && <p className="text-xs text-muted-foreground mt-0.5">{packLabel}{product.gst_rate ? ` · GST ${product.gst_rate}%` : ''}</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[70vh]">
          {/* Key metrics row */}
          <div className="px-5 pt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard
              label="Stock on Hand"
              value={product.stock ?? '—'}
              sub={daysLeft != null ? `~${daysLeft} days left` : undefined}
              valueClass={isOOS ? 'text-red-600' : isLowStock ? 'text-amber-600' : ''}
            />
            <StatCard
              label="Margin %"
              value={fmtPct(marginValue)}
              sub={wac && wasp ? `Buy ${fmt(wac)} · Sell ${fmt(wasp)}` : undefined}
              valueClass={marginColor}
            />
            <StatCard
              label="Velocity"
              value={velocity > 0 ? `${velocity.toFixed(2)}/day` : '—'}
              sub="30-day weighted avg"
              valueClass={velocity > 0 ? 'text-foreground' : 'text-muted-foreground'}
            />
          </div>

          {/* Buy vs Sell price chart */}
          {chartData && (
            <div className="px-5 pt-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Buy vs Sell Price</p>
              <div className="bg-muted/30 rounded-xl p-3">
                <ResponsiveContainer width="100%" height={110}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 32, top: 4, bottom: 4 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      formatter={(v) => [`₹${v}`, '']}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={28}>
                      {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {marginValue != null && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${marginValue < 0 ? 'bg-red-500' : marginValue < 10 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(100, Math.max(0, marginValue))}%` }}
                      />
                    </div>
                    <span className={`text-xs font-semibold ${marginColor}`}>{fmtPct(marginValue)} margin</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performance */}
          {(topInfo || reorderInfo) && (
            <div className="px-5 pt-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Performance (last 90 days)</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatCard label="Qty Sold" value={topInfo?.quantity_sold != null ? fmtQty(topInfo.quantity_sold) : '—'} />
                <StatCard label="Revenue"  value={topInfo?.revenue != null ? fmt(topInfo.revenue) : '—'} />
                <StatCard
                  label="Rev at Risk"
                  value={revAtRisk > 0 ? fmt(revAtRisk) : '—'}
                  valueClass={revAtRisk > 0 ? 'text-red-600' : ''}
                />
              </div>
            </div>
          )}

          {/* Risk flags */}
          {(isOOS || hasLeakage || isDead) && (
            <div className="px-5 pt-4 pb-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Risk Flags</p>
              {isOOS && (
                <RiskBadge
                  icon={Flame}
                  color="red"
                  label="Out of Stock"
                  detail={revAtRisk > 0 ? `Losing ~${fmt(revAtRisk)} in potential revenue` : 'No stock on hand'}
                />
              )}
              {isLowStock && !isOOS && (
                <RiskBadge
                  icon={AlertTriangle}
                  color="amber"
                  label="Low Stock"
                  detail={`${product.stock} units left · reorder at ${reorderInfo.reorder_level}`}
                />
              )}
              {hasLeakage && (
                <RiskBadge
                  icon={TrendingDown}
                  color="orange"
                  label="Margin Leakage"
                  detail={leakageInfo.loss_per_unit > 0 ? `Selling ₹${leakageInfo.loss_per_unit.toFixed(2)}/unit below cost` : 'Projected future loss detected'}
                />
              )}
              {isDead && (
                <RiskBadge
                  icon={Clock}
                  color="amber"
                  label="Dead Stock"
                  detail={`₹${deadInfo.holding_cost?.toLocaleString('en-IN') ?? '—'} locked · ${deadInfo.days_since_last_sale ?? '?'} days without a sale`}
                />
              )}
            </div>
          )}

          {/* Padding if no risk flags */}
          {!isOOS && !hasLeakage && !isDead && <div className="pb-4" />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailModal;
